import type {
  CompiledScript,
  Instruction,
  TextSegment,
} from "./ast";
import { evalExpr, toBool, toNumber } from "./expr";
import { CommandRegistry, type ExecContext } from "./registry";
import { StandardFunctions, VariableStore } from "./runtime";

export interface ScriptLoader {
  /**
   * filename(拡張子なし)を渡すと、その .pix ファイルをパース・コンパイル済みで返す。
   */
  load(filename: string): Promise<CompiledScript>;
}

export interface RunnerOptions {
  registry: CommandRegistry;
  ctx: ExecContext;
  loader: ScriptLoader;
  onMessage?: (speaker: string | null, segments: TextSegment[]) => void;
}

export class Runner {
  private cmdIndex = 0;
  private callStack: Array<{ file: string; cmdIndex: number }> = [];
  private forStack: Array<{
    beginIdx: number;
    varPath: string;
    current: number;
    end: number;
    step: number;
  }> = [];
  private script: CompiledScript;
  private currentFile: string;
  private stopRequested = false;

  readonly vars: VariableStore;
  readonly fns: StandardFunctions;

  constructor(
    initialScript: CompiledScript,
    private opts: RunnerOptions,
  ) {
    this.script = initialScript;
    this.currentFile = initialScript.filename;
    this.fns = new StandardFunctions();
    this.vars = new VariableStore(this.fns);
    // exists() を実行時にオーバーライド
    this.fns.register("exists", (name: unknown) => {
      const path = String(name).split(/[\.[\]]/).filter(Boolean);
      let cur: unknown = this.vars.data;
      for (const k of path) {
        if (cur == null || typeof cur !== "object") return false;
        cur = (cur as Record<string, unknown>)[k];
      }
      return cur !== undefined && cur !== null;
    });
  }

  jumpToLabel(label: string): void {
    const idx = this.script.labels.get(label);
    if (idx === undefined) {
      throw new Error(`ラベルが見つかりません: ${label} (${this.script.filename})`);
    }
    this.cmdIndex = idx;
  }

  async run(): Promise<void> {
    while (!this.stopRequested && this.cmdIndex < this.script.instructions.length) {
      const inst = this.script.instructions[this.cmdIndex]!;
      this.cmdIndex++;
      await this.execute(inst);
    }
  }

  stop(): void {
    this.stopRequested = true;
  }

  getState() {
    return {
      file: this.currentFile,
      cmdIndex: this.cmdIndex,
      callStack: [...this.callStack],
      variables: this.vars.serialize(),
    };
  }

  async setState(state: ReturnType<Runner["getState"]>): Promise<void> {
    if (state.file !== this.currentFile) {
      this.script = await this.opts.loader.load(state.file);
      this.currentFile = state.file;
    }
    this.cmdIndex = state.cmdIndex;
    this.callStack = [...state.callStack];
    this.vars.hydrate(state.variables);
  }

  private async execute(inst: Instruction): Promise<void> {
    switch (inst.type) {
      case "Say": {
        const sayCmdIndex = this.cmdIndex - 1;
        await this.opts.ctx.ui.showMessage(inst.speaker, inst.segments, {
          file: this.currentFile,
          cmdIndex: sayCmdIndex,
        });
        this.opts.onMessage?.(inst.speaker, inst.segments);
        return;
      }
      case "Choice": {
        const filtered = inst.options.filter(
          (o) => !o.condition || toBool(evalExpr(o.condition, this.vars, this.fns)),
        );
        const idxMap = filtered.map((o, i) => ({ option: o, index: i }));
        const selected = await this.opts.ctx.ui.showChoice(
          idxMap.map(({ option, index }) => ({ text: option.text, index })),
        );
        const chosen = idxMap[selected]?.option;
        if (!chosen) throw new Error(`不正な選択: ${selected}`);
        if (chosen.targetFile) {
          await this.navigateTo(chosen.targetFile, chosen.targetLabel);
        } else {
          this.cmdIndex = chosen.targetCmdIndex;
        }
        return;
      }
      case "Jump": {
        if (inst.targetFile) {
          await this.navigateTo(inst.targetFile, inst.targetLabel);
        } else {
          this.cmdIndex = inst.targetCmdIndex;
        }
        return;
      }
      case "JumpIfFalse": {
        const v = evalExpr(inst.condition, this.vars, this.fns);
        if (!toBool(v)) {
          this.cmdIndex = inst.targetCmdIndex;
        }
        return;
      }
      case "Call": {
        this.callStack.push({ file: this.currentFile, cmdIndex: this.cmdIndex });
        if (inst.targetFile) {
          await this.navigateTo(inst.targetFile, inst.targetLabel);
        } else {
          this.cmdIndex = inst.targetCmdIndex;
        }
        return;
      }
      case "Return": {
        const frame = this.callStack.pop();
        if (!frame) {
          this.stopRequested = true;
          return;
        }
        if (frame.file !== this.currentFile) {
          this.script = await this.opts.loader.load(frame.file);
          this.currentFile = frame.file;
        }
        this.cmdIndex = frame.cmdIndex;
        return;
      }
      case "Set": {
        const newVal = computeAssignment(
          inst.op,
          this.vars.get(inst.lvalue),
          evalExpr(inst.expr, this.vars, this.fns),
        );
        this.vars.set(inst.lvalue, newVal);
        return;
      }
      case "Unset": {
        this.vars.unset(inst.lvalue);
        return;
      }
      case "ForBegin": {
        const start = toNumber(evalExpr(inst.start, this.vars, this.fns));
        const end = toNumber(evalExpr(inst.end, this.vars, this.fns));
        const step = toNumber(evalExpr(inst.step, this.vars, this.fns));
        const goingUp = step > 0;
        if ((goingUp && start > end) || (!goingUp && start < end)) {
          this.cmdIndex = inst.exitCmdIndex;
          return;
        }
        this.vars.set([{ kind: "name", name: "local" }, { kind: "name", name: inst.varName }], start);
        this.forStack.push({
          beginIdx: this.cmdIndex - 1,
          varPath: inst.varName,
          current: start,
          end,
          step,
        });
        return;
      }
      case "ForEnd": {
        const frame = this.forStack[this.forStack.length - 1];
        if (!frame) return;
        frame.current += frame.step;
        const inRange = frame.step > 0 ? frame.current <= frame.end : frame.current >= frame.end;
        if (inRange) {
          this.vars.set(
            [{ kind: "name", name: "local" }, { kind: "name", name: frame.varPath }],
            frame.current,
          );
          this.cmdIndex = frame.beginIdx + 1;
        } else {
          this.forStack.pop();
          // 次の命令へ
        }
        return;
      }
      case "Wait": {
        if (inst.mode === "frame") {
          await this.opts.ctx.wait.frame(inst.value ?? 0);
        } else if (inst.mode === "time") {
          await this.opts.ctx.wait.time(inst.value ?? 0);
        } else if (inst.mode === "key") {
          await this.opts.ctx.wait.key();
        }
        // bgm/voice は MVP では何もしない
        return;
      }
      case "Command": {
        const def = this.opts.registry.get(inst.name);
        if (!def) {
          // 未登録コマンドは無視(将来 plugin として登録される可能性)
          console.warn(`[runner] 未登録コマンド @${inst.name} をスキップ(行 ${inst.line})`);
          return;
        }
        await def.execute(inst.args, inst.subcommand, this.opts.ctx);
        return;
      }
      case "End": {
        this.stopRequested = true;
        return;
      }
    }
  }

  private async navigateTo(file: string, label: string): Promise<void> {
    this.script = await this.opts.loader.load(file);
    this.currentFile = file;
    const idx = this.script.labels.get(label);
    if (idx === undefined) {
      throw new Error(`ラベルが見つかりません: ${label} (${file})`);
    }
    this.cmdIndex = idx;
  }
}

function computeAssignment(
  op: "=" | "+=" | "-=" | "*=" | "/=",
  oldValue: unknown,
  newValue: unknown,
): unknown {
  if (op === "=") return newValue;
  const a = toNumber(oldValue);
  const b = toNumber(newValue);
  switch (op) {
    case "+=":
      if (typeof oldValue === "string" || typeof newValue === "string") {
        return String(oldValue ?? "") + String(newValue ?? "");
      }
      return a + b;
    case "-=":
      return a - b;
    case "*=":
      return a * b;
    case "/=":
      if (b === 0) throw new Error("0 で除算しました");
      return a / b;
  }
}
